import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { calculateTermination, type Competence, type TerminationType } from '@amigodoclt/core';
import { Icon } from '../components/Icon.js';
import { NumberField, RangeField, SelectField, Stepper, ToggleRow } from '../components/Field.js';
import { ResultCard } from '../components/ResultCard.js';
import { calculators, terminationTypes, type Calculator as Kind, type Field, type Flags, type Values } from '../lib/calculators.js';
import { useSalaryForm } from '../lib/salary-form.js';
import { money } from '../lib/format.js';
import { useReveal } from '../lib/motion.js';

const marksFor = (min: number, max: number) =>
  [...new Set([min, Math.round(min + (max - min) / 3), Math.round(min + (2 * (max - min)) / 3), max])];

function CalculatorField({ field, value, onChange }: { field: Field; value: number; onChange: (value: number) => void }) {
  if (field.kind === 'range' && field.max !== undefined) {
    // A régua é o controle principal da página; o protótipo dá a linha inteira a ela.
    return (
      <div className="span-2">
        <RangeField label={field.label} value={value} onChange={onChange} min={field.min ?? 0} max={field.max} step={field.step ?? 1} marks={marksFor(field.min ?? 0, field.max)} />
      </div>
    );
  }
  if (field.kind === 'stepper') {
    return <Stepper label={field.label} value={value} onChange={onChange} min={field.min ?? 0} max={field.max} step={field.step ?? 1} />;
  }
  return <NumberField label={field.label} value={value} onChange={onChange} min={field.min ?? 0} max={field.max} step={field.step ?? .01} suffix={field.kind === 'percent' ? '%' : 'R$'} />;
}

export function CalculatorPage({ kind }: { kind: Kind }) {
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);

  const calculator = calculators[kind];
  const [competenceValue, setCompetence] = useState<Competence>(2026);
  const [terminationType, setTerminationType] = useState<TerminationType>('without-cause');
  const [own, setOwn] = useState<Values>(() => Object.fromEntries(calculator.fields.map((field) => [field.key, field.initial])));
  const [flags, setFlags] = useState<Flags>(() => Object.fromEntries((calculator.toggles ?? []).map((toggle) => [toggle.key, false])));
  const [form] = useSalaryForm();

  // O salário-base não é campo da página: vem do formulário compartilhado, editável na pílula fixa.
  const values = useMemo(
    () => (calculator.usesSalaryBase ? { ...own, monthlySalary: form.base } : own),
    [calculator.usesSalaryBase, form.base, own],
  );

  const computed = useMemo(
    () => (kind === 'rescisao'
      ? calculateTermination({
        competence: competenceValue, monthlySalary: values.monthlySalary ?? 0, type: terminationType,
        workedDays: values.workedDays, proportionalVacationMonths: values.proportionalVacationMonths,
        thirteenthMonths: values.thirteenthMonths, fgtsBalance: values.fgtsBalance,
      })
      : calculator.run(values, competenceValue, flags)),
    [calculator, competenceValue, flags, kind, terminationType, values],
  );

  return (
    <main className="page" ref={root}>
      <div className="calc-tabs" role="tablist" aria-label="Calculadoras" data-reveal>
        {Object.entries(calculators).map(([slug, entry]) => (
          <Link key={slug} to={`/calculos/${slug}`} role="tab" aria-selected={slug === kind} className={`calc-tab${slug === kind ? ' active' : ''}`}>
            <Icon name={entry.icon} size={18} fill={slug === kind} />{entry.title}
          </Link>
        ))}
      </div>

      <div className="page-head" data-reveal>
        <div className="page-head-copy">
          <p className="eyebrow">Estimativa {competenceValue}</p>
          <div className="title-row">
            <span className="title-icon"><Icon name={calculator.icon} size={34} fill /></span>
            <h1 className="page-title">{calculator.title}</h1>
          </div>
          <p className="lead">{calculator.intro}</p>
        </div>
      </div>

      <div className="page-layout">
        <form className="glass-form glass" onSubmit={(event) => event.preventDefault()}>
          <section className="form-section" data-reveal>
            <div className="field-grid">
              <SelectField
                label="Competência" value={String(competenceValue)}
                onChange={(value) => setCompetence(Number(value) as Competence)}
                options={[{ value: '2026', label: '2026' }, { value: '2025', label: '2025' }]}
              />
              {kind === 'rescisao'
                ? <SelectField label="Modalidade" value={terminationType} onChange={(value) => setTerminationType(value as TerminationType)} options={terminationTypes} />
                : null}
              {calculator.fields.map((field) => (
                <CalculatorField
                  key={field.key} field={field} value={own[field.key] ?? 0}
                  onChange={(value) => setOwn((current) => ({ ...current, [field.key]: value }))}
                />
              ))}
            </div>
            {calculator.toggles?.map((toggle) => (
              <ToggleRow
                key={toggle.key} label={toggle.label} icon={toggle.icon} value={flags[toggle.key] ?? false}
                onChange={(value) => setFlags((current) => ({ ...current, [toggle.key]: value }))}
              />
            ))}
            {calculator.usesSalaryBase ? (
              <p className="helper"><Icon name="payments" size={16} />Calculado sobre o salário-base de {money(form.base)}, editável no atalho do topo — o mesmo da calculadora de salário.</p>
            ) : null}
            <p className="helper"><Icon name="info" size={16} />CCT, impostos PJ e encargos variáveis devem ser informados manualmente.</p>
          </section>
        </form>

        <ResultCard data={computed} subtitle={`${calculator.title} · ${competenceValue}`} className="calc-result" grossLabel={calculator.grossLabel} />
      </div>
    </main>
  );
}
